/**
 * Decision Brief — Data Service
 *
 * All database interactions for the Decision Brief product surface.
 * Uses the existing Supabase client with RLS enforced at the database layer.
 */

import { getSupabaseClient } from '../../lib/supabaseClient';
import type {
  Brief,
  BriefCriteria,
  BriefOption,
  BriefProposal,
  BriefScore,
  BriefDecision,
  BriefPublicView,
  BriefOptionWithProposal,
  SubmitTokenView,
  BriefStatus,
  DecisionType,
  DecisionScope,
  PromotionType,
  BriefResolutionType,
} from '../../types/brief';

// ── Brief CRUD ────────────────────────────────────────────────────────────────

export async function listBriefs(workspaceId: string): Promise<Brief[]> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('briefs')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Failed to list briefs: ${error.message}`);
  return data as Brief[];
}

/** What the Decisions list needs per brief, in three queries instead of one per row. */
export interface BriefProgress {
  brief: Brief;
  options: Array<Pick<BriefOptionWithProposal, 'name' | 'owner_name' | 'proposal'>>;
  criteriaCount: number;
  decision: Pick<BriefDecision, 'selected_option_ids' | 'revisit_when'> | null;
}

export async function listBriefsWithProgress(workspaceId: string): Promise<BriefProgress[]> {
  const briefs = await listBriefs(workspaceId);
  if (briefs.length === 0) return [];
  const client = getSupabaseClient();
  const ids = briefs.map((b) => b.id);

  const [optionsRes, criteriaRes, decisionsRes] = await Promise.all([
    client.from('brief_options').select('id, brief_id, name, owner_name').in('brief_id', ids),
    client.from('brief_criteria').select('id, brief_id').in('brief_id', ids),
    client.from('brief_decisions').select('brief_id, selected_option_ids, revisit_when').in('brief_id', ids),
  ]);
  if (optionsRes.error) throw new Error(`Failed to load options: ${optionsRes.error.message}`);
  if (criteriaRes.error) throw new Error(`Failed to load criteria: ${criteriaRes.error.message}`);
  if (decisionsRes.error) throw new Error(`Failed to load decisions: ${decisionsRes.error.message}`);

  const optionRows = (optionsRes.data ?? []) as Array<{ id: string; brief_id: string; name: string; owner_name: string | null }>;
  const optionIds = optionRows.map((o) => o.id);
  const submitted = new Set<string>();
  if (optionIds.length > 0) {
    const { data: proposals, error } = await client.from('brief_proposals').select('option_id').in('option_id', optionIds);
    if (error) throw new Error(`Failed to load proposals: ${error.message}`);
    for (const p of (proposals ?? []) as Array<{ option_id: string }>) submitted.add(p.option_id);
  }

  const criteriaByBrief = new Map<string, number>();
  for (const c of (criteriaRes.data ?? []) as Array<{ brief_id: string }>) {
    criteriaByBrief.set(c.brief_id, (criteriaByBrief.get(c.brief_id) ?? 0) + 1);
  }
  const decisionByBrief = new Map<string, { selected_option_ids: string[]; revisit_when: string | null }>();
  for (const d of (decisionsRes.data ?? []) as Array<{ brief_id: string; selected_option_ids: string[] | null; revisit_when?: string | null }>) {
    decisionByBrief.set(d.brief_id, { selected_option_ids: d.selected_option_ids ?? [], revisit_when: d.revisit_when ?? null });
  }

  return briefs.map((brief) => ({
    brief,
    options: optionRows
      .filter((o) => o.brief_id === brief.id)
      .map((o) => ({ name: o.name, owner_name: o.owner_name, proposal: submitted.has(o.id) ? ({} as BriefOptionWithProposal['proposal']) : null })),
    criteriaCount: criteriaByBrief.get(brief.id) ?? 0,
    decision: decisionByBrief.get(brief.id) ?? null,
  }));
}

export async function getBrief(id: string): Promise<Brief | null> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('briefs')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // not found
    throw new Error(`Failed to get brief: ${error.message}`);
  }
  return data as Brief;
}

/**
 * Create a decision as a `draft` — durable from the first keystroke.
 * Routes through `create_brief` (migration 794): creator = the caller,
 * membership checked server-side, one audit row.
 */
export async function createBrief(
  workspaceId: string,
  draft: { title: string; decision_authority?: string | null; deadline?: string | null }
): Promise<Brief> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('create_brief', {
    p_workspace_id: workspaceId,
    p_title: draft.title.trim(),
    p_decision_authority: draft.decision_authority?.trim() || null,
    p_deadline: draft.deadline || null,
  });
  if (error) throw new Error(`Failed to create decision: ${error.message}`);
  return data as Brief;
}

/** The fields the decision page autosaves, one at a time. */
export type BriefSetupPatch = Partial<{
  title: string;
  context: string | null;
  deadline: string | null;
  decision_authority: string | null;
  voice_holders: string | null;
  observers: string | null;
  decision_type: DecisionType | null;
  decision_scope: DecisionScope | null;
  scope_label: string | null;
  tags: string[];
}>;

/**
 * Update the descriptive fields of a brief. Routes through
 * `update_brief_setup` (migration 794) — `briefs` has no client UPDATE
 * privilege any more; status, creator, tokens and promotion are other RPCs.
 */
export async function updateBrief(id: string, patch: BriefSetupPatch): Promise<Brief> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('update_brief_setup', {
    p_brief_id: id,
    p_patch: patch,
  });
  if (error) throw new Error(`Failed to save: ${error.message}`);
  return data as Brief;
}

/**
 * Advance a brief's status through the validated FSM (§2.3a).
 *
 * Routes through the `advance_brief_status` SECURITY DEFINER RPC which:
 *  - validates the caller is the brief creator
 *  - validates the FSM transition (draft→collecting→comparing, any→archived)
 *  - never moves a brief to decided or deferred: recordBriefDecision does (910)
 *  - persists atomically and appends a brief_events audit row
 *
 * Direct client UPDATE of brief.status is prohibited per the build contract
 * and, since migration 793, impossible.
 */
export async function advanceBriefStatus(id: string, status: BriefStatus): Promise<Brief> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('advance_brief_status', {
    p_brief_id: id,
    p_status: status,
  });

  if (error) throw new Error(`Status transition failed: ${error.message}`);
  return data as Brief;
}

/**
 * draft → collecting, the moment proposal owners are asked. The RPC
 * (migration 794) refuses with a plain sentence when there are fewer than
 * two options or no criteria.
 */
export async function openBriefForProposals(id: string): Promise<Brief> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('open_brief_for_proposals', { p_brief_id: id });
  if (error) throw new Error(error.message);
  return data as Brief;
}

/** Stamp the Plan of Record link on a decided brief (idempotent; migration 794). */
export async function promoteBrief(
  id: string,
  decisionId: string,
  promotionType: PromotionType,
  promotionNote: string | null
): Promise<Brief> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('promote_brief_to_record', {
    p_brief_id: id,
    p_decision_id: decisionId,
    p_promotion_type: promotionType,
    p_promotion_note: promotionNote,
  });
  if (error) throw new Error(`Could not put this decision on the plan: ${error.message}`);
  return data as Brief;
}

/** Ask the server to email proposal owners their private links (best-effort). */
export async function sendBriefInvites(
  briefId: string,
  optionIds: string[],
  kind: 'invite' | 'remind'
): Promise<{ sent: number; skipped: Array<{ option_id: string; reason: string }> }> {
  const client = getSupabaseClient();
  const { data, error } = await client.functions.invoke('send-brief-invites', {
    body: { brief_id: briefId, option_ids: optionIds, kind },
  });
  if (error) return { sent: 0, skipped: optionIds.map((option_id) => ({ option_id, reason: error.message })) };
  const result = (data ?? {}) as { sent?: number; skipped?: Array<{ option_id: string; reason: string }> };
  return { sent: result.sent ?? 0, skipped: result.skipped ?? [] };
}

/** Record that owners were sent their link (brief_options carries its own member RLS). */
export async function markOptionsInvited(optionIds: string[], kind: 'invite' | 'remind'): Promise<void> {
  if (optionIds.length === 0) return;
  const client = getSupabaseClient();
  const now = new Date().toISOString();
  const { error } = await client
    .from('brief_options')
    .update(kind === 'invite' ? { invited_at: now, last_reminded_at: now } : { last_reminded_at: now })
    .in('id', optionIds);
  if (error) throw new Error(`Failed to record the send: ${error.message}`);
}

// ── Criteria ──────────────────────────────────────────────────────────────────

export async function listCriteria(briefId: string): Promise<BriefCriteria[]> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('brief_criteria')
    .select('*')
    .eq('brief_id', briefId)
    .order('sort_order');

  if (error) throw new Error(`Failed to list criteria: ${error.message}`);
  return data as BriefCriteria[];
}

export async function upsertCriteria(
  briefId: string,
  criteria: Array<{ id?: string; name: string; description: string; weight: number; sort_order: number }>
): Promise<BriefCriteria[]> {
  const client = getSupabaseClient();
  const rows = criteria.map((c, i) => ({
    ...(c.id ? { id: c.id } : {}),
    brief_id: briefId,
    name: c.name.trim(),
    description: c.description?.trim() || null,
    weight: c.weight,
    sort_order: c.sort_order ?? i,
  }));

  const { data, error } = await client
    .from('brief_criteria')
    .upsert(rows, { onConflict: 'id' })
    .select();

  if (error) throw new Error(`Failed to save criteria: ${error.message}`);
  return data as BriefCriteria[];
}

export async function deleteCriteria(id: string): Promise<void> {
  const client = getSupabaseClient();
  const { error } = await client.from('brief_criteria').delete().eq('id', id);
  if (error) throw new Error(`Failed to delete criterion: ${error.message}`);
}

// ── Options ───────────────────────────────────────────────────────────────────

export async function listOptions(briefId: string): Promise<BriefOption[]> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('brief_options')
    .select('*')
    .eq('brief_id', briefId)
    .order('sort_order');

  if (error) throw new Error(`Failed to list options: ${error.message}`);
  return data as BriefOption[];
}

export async function createOption(
  briefId: string,
  option: { name: string; owner_name?: string; owner_email?: string; sort_order?: number }
): Promise<BriefOption> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('brief_options')
    .insert({
      brief_id: briefId,
      name: option.name.trim(),
      owner_name: option.owner_name?.trim() || null,
      owner_email: option.owner_email?.trim() || null,
      sort_order: option.sort_order ?? 0,
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create option: ${error.message}`);
  return data as BriefOption;
}

export async function updateOption(
  id: string,
  updates: Partial<Pick<BriefOption, 'name' | 'owner_name' | 'owner_email' | 'sort_order'>>
): Promise<BriefOption> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('brief_options')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update option: ${error.message}`);
  return data as BriefOption;
}

export async function deleteOption(id: string): Promise<void> {
  const client = getSupabaseClient();
  const { error } = await client.from('brief_options').delete().eq('id', id);
  if (error) throw new Error(`Failed to delete option: ${error.message}`);
}

// ── Scores ────────────────────────────────────────────────────────────────────

export async function listScoresForBrief(briefId: string): Promise<BriefScore[]> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('brief_scores')
    .select(`
      *,
      brief_options!inner(brief_id)
    `)
    .eq('brief_options.brief_id', briefId);

  if (error) throw new Error(`Failed to list scores: ${error.message}`);
  return (data as (BriefScore & { brief_options: unknown })[]).map(
    ({ brief_options: _drop, ...score }) => score as BriefScore
  );
}

export async function upsertScore(
  optionId: string,
  criteriaId: string,
  score: number,
  userId: string,
  note?: string
): Promise<BriefScore> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('brief_scores')
    .upsert(
      {
        option_id: optionId,
        criteria_id: criteriaId,
        score,
        note: note ?? null,
        scored_by: userId,
        scored_at: new Date().toISOString(),
      },
      { onConflict: 'option_id,criteria_id' }
    )
    .select()
    .single();

  if (error) throw new Error(`Failed to upsert score: ${error.message}`);
  return data as BriefScore;
}

// ── Decision ──────────────────────────────────────────────────────────────────

export async function getDecision(briefId: string): Promise<BriefDecision | null> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('brief_decisions')
    .select('*')
    .eq('brief_id', briefId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to get decision: ${error.message}`);
  }
  return data as BriefDecision;
}

/**
 * Record how the brief was resolved (migration 910). One transaction writes the
 * decision row and moves the brief to `decided` — or `deferred` for a deferral —
 * with an audit event. Only the brief's creator may call it; the RPC checks the
 * shape of each resolution and refuses with a plain sentence.
 */
export async function recordBriefDecision(
  briefId: string,
  decision: {
    resolution_type: BriefResolutionType;
    selected_option_ids: string[];
    selected_rationale: string;
    rejected_rationale?: string | null;
    conditions?: string | null;
    signal_for_change?: string | null;
    revisit_when?: string | null;
  }
): Promise<BriefDecision> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('record_brief_decision', {
    p_brief_id: briefId,
    p_resolution_type: decision.resolution_type,
    p_selected_option_ids: decision.selected_option_ids,
    p_selected_rationale: decision.selected_rationale,
    p_rejected_rationale: decision.rejected_rationale ?? null,
    p_conditions: decision.conditions ?? null,
    p_signal_for_change: decision.signal_for_change ?? null,
    p_revisit_when: decision.revisit_when ?? null,
  });
  if (error) throw new Error(error.message);
  return data as BriefDecision;
}

/**
 * Return a decided or deferred brief to comparing (migration 910). The reason
 * is required and kept, with the decision being set aside, in the brief's audit
 * trail. Refused for a brief already put on the plan.
 */
export async function reopenBrief(briefId: string, reason: string): Promise<Brief> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('reopen_brief', { p_brief_id: briefId, p_reason: reason });
  if (error) throw new Error(error.message);
  return data as Brief;
}

// ── Enriched brief load (for BriefWorkspace) ──────────────────────────────────

export async function loadBriefWorkspace(briefId: string): Promise<{
  brief: Brief;
  criteria: BriefCriteria[];
  options: BriefOptionWithProposal[];
  decision: BriefDecision | null;
}> {
  const client = getSupabaseClient();

  const [briefResult, criteriaResult, optionsResult, decisionsResult] = await Promise.all([
    client.from('briefs').select('*').eq('id', briefId).single(),
    client.from('brief_criteria').select('*').eq('brief_id', briefId).order('sort_order'),
    client
      .from('brief_options')
      .select('*, brief_proposals(*), brief_scores(*)')
      .eq('brief_id', briefId)
      .order('sort_order'),
    client.from('brief_decisions').select('*').eq('brief_id', briefId).single(),
  ]);

  if (briefResult.error) throw new Error(`Failed to load brief: ${briefResult.error.message}`);
  if (criteriaResult.error) throw new Error(`Failed to load criteria: ${criteriaResult.error.message}`);
  if (optionsResult.error) throw new Error(`Failed to load options: ${optionsResult.error.message}`);

  const rawOptions = optionsResult.data as Array<
    BriefOption & { brief_proposals: BriefProposal[]; brief_scores: BriefScore[] }
  >;

  const options: BriefOptionWithProposal[] = rawOptions.map((o) => ({
    id: o.id,
    brief_id: o.brief_id,
    name: o.name,
    owner_name: o.owner_name,
    invited_at: o.invited_at ?? null,
    last_reminded_at: o.last_reminded_at ?? null,
    owner_email: o.owner_email,
    submit_token: o.submit_token,
    sort_order: o.sort_order,
    created_at: o.created_at,
    proposal: o.brief_proposals?.[0] ?? null,
    scores: o.brief_scores ?? [],
  }));

  const decision =
    decisionsResult.error?.code === 'PGRST116'
      ? null
      : decisionsResult.error
      ? null
      : (decisionsResult.data as BriefDecision);

  return {
    brief: briefResult.data as Brief,
    criteria: criteriaResult.data as BriefCriteria[],
    options,
    decision,
  };
}

// ── Public RPCs (no auth) ─────────────────────────────────────────────────────

export async function getBriefByViewToken(token: string): Promise<BriefPublicView | null> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_brief_by_view_token', { p_token: token });
  if (error) throw new Error(`Failed to load brief view: ${error.message}`);
  if (!data) return null;
  // json_agg over no rows is null: a brief decided before any proposal came in
  // has proposals = null, and the public record crashed reading it.
  const view = data as BriefPublicView;
  return { ...view, options: view.options ?? [], proposals: view.proposals ?? [], criteria: view.criteria ?? [] };
}

export async function getOptionBySubmitToken(token: string): Promise<SubmitTokenView | null> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_option_by_submit_token', { p_token: token });
  if (error) throw new Error(`Failed to load submission context: ${error.message}`);
  return (data as SubmitTokenView) ?? null;
}

export async function submitBriefProposal(
  token: string,
  payload: {
    rationale: string;
    investment: string;
    outcome: string;
    assumptions: string[];
    opportunity: string;
    risk: string;
    notes?: string;
  }
): Promise<{ proposal_id: string; option_id: string }> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('submit_brief_proposal', {
    p_token: token,
    p_rationale: payload.rationale,
    p_investment: payload.investment,
    p_outcome: payload.outcome,
    p_assumptions: payload.assumptions,
    p_opportunity: payload.opportunity,
    p_risk: payload.risk,
    p_notes: payload.notes ?? null,
  });

  if (error) throw new Error(`Failed to submit proposal: ${error.message}`);
  return data as { proposal_id: string; option_id: string };
}

export async function getWorkspaceBriefCount(workspaceId: string): Promise<number> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_workspace_brief_count', {
    p_workspace_id: workspaceId,
  });
  if (error) throw new Error(`Failed to get brief count: ${error.message}`);
  return (data as number) ?? 0;
}

// ── Computed helpers ──────────────────────────────────────────────────────────

/**
 * Computes the weighted total score for an option across all criteria.
 * Returns null if no scores exist yet.
 */
export function computeWeightedScore(
  option: BriefOptionWithProposal,
  criteria: BriefCriteria[]
): number | null {
  if (option.scores.length === 0) return null;

  let totalWeight = 0;
  let weightedSum = 0;

  for (const criterion of criteria) {
    const score = option.scores.find((s) => s.criteria_id === criterion.id);
    if (score) {
      weightedSum += score.score * criterion.weight;
      totalWeight += criterion.weight;
    }
  }

  if (totalWeight === 0) return null;
  return Math.round((weightedSum / totalWeight) * 10) / 10;
}

/**
 * Returns options sorted by their weighted score descending.
 * Unscored options appear last.
 */
export function rankOptions(
  options: BriefOptionWithProposal[],
  criteria: BriefCriteria[]
): Array<BriefOptionWithProposal & { weightedScore: number | null; rank: number | null }> {
  const scored = options.map((o) => ({
    ...o,
    weightedScore: computeWeightedScore(o, criteria),
  }));

  const withScores = scored.filter((o) => o.weightedScore !== null);
  const withoutScores = scored.filter((o) => o.weightedScore === null);

  withScores.sort((a, b) => (b.weightedScore! - a.weightedScore!));

  return [
    ...withScores.map((o, i) => ({ ...o, rank: i + 1 })),
    ...withoutScores.map((o) => ({ ...o, rank: null })),
  ];
}
